/**
 * Life Tab (Umr) — Premium life management.
 * Sections: Countdown → Calendar → Timeline → Goals → Balance → Stats
 */

import { memo, useMemo, useState } from 'react'
import { Settings } from 'lucide-react'
import { motion } from 'framer-motion'
import { useAuthStore } from '@turnifly/shared/stores'
import { useRealtimeCountdown, useFamousMilestones, useLifeAchievements } from '@turnifly/shared/hooks'
import { useProfileGoals } from '@turnifly/shared/hooks'
import { LifeSetupPrompt } from './life-setup-prompt'
import { LifeSettingsSheet } from './life-settings-sheet'
import { TimeCountdown } from './time-countdown'
import { UmrCalendar } from './umr-calendar'
import { LifeTimeline } from './life-timeline'
import { LifeBalance } from './life-balance'
import { LifeStats } from './life-stats'
import { GoalsPreview } from './goals-preview'

export const LifeTab = memo(function LifeTab() {
  const user = useAuthStore((s) => s.user)
  const [showSettings, setShowSettings] = useState(false)

  const birthDate = user?.birth_date
  const lifeExpectancy = user?.life_expectancy ?? 80
  const timezone = user?.timezone ?? 'Asia/Tashkent'

  const timeState = useRealtimeCountdown(timezone, birthDate, lifeExpectancy)
  const { data: famousMilestones = [] } = useFamousMilestones()
  const { data: userAchievements = [] } = useLifeAchievements()
  const { data: goals = [] } = useProfileGoals(user?.id)

  const activeGoals = useMemo(
    () => goals.filter((g) => g.status === 'in_progress' || g.status === 'planned').slice(0, 3),
    [goals],
  )

  if (!birthDate) return <LifeSetupPrompt />

  return (
    <div className="space-y-3 pb-4">
      {/* Settings gear */}
      <div className="flex justify-end">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => setShowSettings(true)}
          className="flex items-center justify-center w-8 h-8 rounded-full bg-white/[0.04] border border-white/[0.06] tap-highlight-transparent"
        >
          <Settings className="w-3.5 h-3.5 text-hint" />
        </motion.button>
      </div>

      {/* Real-time countdown */}
      <TimeCountdown timeState={timeState} />

      {/* Umr Calendar */}
      <UmrCalendar
        birthDate={birthDate}
        lifeExpectancy={lifeExpectancy}
        famousMilestones={famousMilestones}
        userAchievements={userAchievements}
      />

      {/* Life Timeline */}
      <LifeTimeline
        birthDate={birthDate}
        lifeExpectancy={lifeExpectancy}
        userAchievements={userAchievements}
      />

      {/* Goals connection */}
      {activeGoals.length > 0 && <GoalsPreview goals={activeGoals} />}

      {/* Life Balance Wheel */}
      <LifeBalance />

      {/* Life Stats */}
      <LifeStats birthDate={birthDate} lifeExpectancy={lifeExpectancy} />

      {/* Settings sheet */}
      <LifeSettingsSheet isOpen={showSettings} onClose={() => setShowSettings(false)} />
    </div>
  )
})
